import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-[var(--font-family--sans)] font-medium transition-all " +
  "outline-none active:scale-95 " +
  "disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // bg=black | hover=btn-primary-hover | focus=ring | disabled=epic-neutral-8
        primary:
          "bg-black text-white " +
          "hover:bg-btn-primary-hover " +
          "focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:bg-epic-neutral-8 disabled:text-white",

        // bg=brand | hover=btn-brand-hover | focus=ring-brand | disabled=epic-neutral-8
        brand:
          "bg-brand text-white " +
          "hover:bg-btn-brand-hover " +
          "focus:ring focus:ring-brand focus:ring-offset-0 " +
          "disabled:bg-epic-neutral-8 disabled:text-white",

        // bg=btn-secondary-bg | hover=btn-secondary-hover | disabled=epic-neutral-3/text-9
        secondary:
          "bg-btn-secondary-bg text-foreground " +
          "hover:bg-btn-secondary-hover " +
          "focus:bg-btn-secondary-hover focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:bg-epic-neutral-3 disabled:text-epic-neutral-9",

        // border=foreground | bg=btn-ghost-bg | hover=btn-ghost-hover
        // disabled: borderâ†’epic-neutral-8, textâ†’epic-neutral-11
        outline:
          "border border-foreground bg-btn-ghost-bg text-foreground " +
          "hover:bg-btn-ghost-hover " +
          "focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:border-epic-neutral-8 disabled:bg-btn-ghost-bg disabled:text-epic-neutral-11",

        // bg=btn-ghost-bg | hover=btn-ghost-hover | disabled textâ†’epic-neutral-8
        ghost:
          "bg-btn-ghost-bg text-foreground " +
          "hover:bg-btn-ghost-hover " +
          "focus:bg-btn-ghost-hover focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:bg-btn-ghost-bg disabled:text-epic-neutral-8",

        // bg=destructive | hover=btn-destructive-hover | focus=ring-destructive/20 | disabled=red-300
        destructive:
          "bg-destructive text-destructive-foreground " +
          "hover:bg-btn-destructive-hover " +
          "focus:ring focus:ring-destructive/20 focus:ring-offset-0 " +
          "disabled:bg-red-300 disabled:text-white",

        // bg=transparent | text=slate/700(#334155) | hover=black/5+epic-neutral-11
        // radius=rounded-lg via compound variant (8px vs 4px for other variants)
        "ghost-muted":
          "bg-transparent text-slate-700 " +
          "hover:bg-black/5 hover:text-epic-neutral-11 " +
          "focus:ring focus:ring-ring focus:ring-offset-0",
      },
      size: {
        // h=40px | py=8px(semantic/xs) | px=24px(semantic/xl) | gap=8px
        large:   "h-10 py-2 px-6 gap-2 text-sm",
        // h=36px | py=8px(semantic/xs) | px=16px(semantic/md) | gap=8px
        regular: "h-9  py-2 px-4 gap-2 text-sm",
        // h=32px | py=4px(semantic/2xs) | px=12px(absolute/3) | gap=8px
        small:   "h-8  py-1 px-3 gap-2 text-sm",
        // h=24px | py=4px(semantic/2xs) | px=8px(semantic/xs) | gap=6px(absolute/1.5)
        mini:    "h-6  py-1 px-2 gap-1.5 text-xs",
      },
      rounded: {
        // semantic/rounded-sm = absolute/radius-4 = 4px
        default: "rounded-sm",
        full:    "rounded-full",
      },
    },
    compoundVariants: [
      // ghost-muted uses semantic/rounded-lg (8px) instead of rounded-sm (4px)
      { variant: "ghost-muted", rounded: "default", class: "rounded-lg" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "regular",
      rounded: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, leftIcon, rightIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      >
        {leftIcon && <span className="flex items-center">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="flex items-center">{rightIcon}</span>}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
